{
  class NbBundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.items = new Map();
    }

    connectedCallback() {
      this.tiers = this._readJson('tiers') || [];
      this.galleryVariants = this._readJson('gallery-variants') || [];
      this.products = this._readJson('products') || {};
      this.productIndex = Object.assign({}, this.products);
      this.primaryProductIds = new Set((this._readJson('primary-product-ids') || []).map(String));
      this.giftRules = this._parseGiftRules(this._readJson('dk-discount-configs') || []);
      // Section setting: whether primary (first collection) items get the tier discount.
      this.discountPrimary = this.dataset.discountPrimary === 'true';

      this.currentSlide = 0;
      this.itemsEl = this.querySelector('[data-slot="items"]');
      this.emptyEl = this.querySelector('[data-slot="empty"]');
      this.countEl = this.querySelector('[data-slot="count"]');
      this.summaryEl = this.querySelector('[data-slot="summary"]');
      this.subtotalEl = this.querySelector('[data-slot="subtotal"]');
      this.savingsRowEl = this.querySelector('[data-slot="savings-row"]');
      this.savingsEl = this.querySelector('[data-slot="savings"]');
      this.totalEl = this.querySelector('[data-slot="total"]');
      this.atcEl = this.querySelector('[data-action="add-to-cart"]');
      this.rowTpl = this.querySelector('template[data-tpl="item-row"]');
      this.stageEl = this.querySelector('.nb-bb__stage');
      this.stageTrackEl = this.querySelector('[data-slot="stage-track"]');
      this.thumbButtons = this.querySelectorAll('.nb-bb__thumb');
      this.progressFill = this.querySelector('.nb-bb__progress-fill');
      this.tierDots = this.querySelectorAll('.nb-bb__progress-dot');
      this.tierLabels = this.querySelectorAll('.nb-bb__progress-tier-labels span');
      this.qvEl = this.querySelector('[data-slot="quickview"]');

      this.addEventListener('click', this._onClick.bind(this));
      this.addEventListener('change', this._onChange.bind(this));
      if (this.stageTrackEl) {
        this.stageTrackEl.addEventListener('scroll', this._onStageScroll.bind(this), { passive: true });
      }

      this._onQuickviewKeyup = (event) => {
        if (event.key === 'Escape') this._closeQuickview();
      };
      document.addEventListener('keyup', this._onQuickviewKeyup);

      this._updateViewportOffset = this._updateViewportOffset.bind(this);
      this._updateViewportOffset();
      window.addEventListener('resize', this._updateViewportOffset);
      window.addEventListener('load', this._updateViewportOffset);
      if ('ResizeObserver' in window) {
        const announcement = document.querySelector('.announcement-bar-spacer');
        const header = document.querySelector('.nb-header');
        this._viewportRO = new ResizeObserver(this._updateViewportOffset);
        if (announcement) this._viewportRO.observe(announcement);
        if (header) this._viewportRO.observe(header);
      }

      this._render();
      this._initCards();
    }

    disconnectedCallback() {
      document.removeEventListener('keyup', this._onQuickviewKeyup);
      window.removeEventListener('resize', this._updateViewportOffset);
      window.removeEventListener('load', this._updateViewportOffset);
      if (this._viewportRO) this._viewportRO.disconnect();
      document.body.classList.remove('overflow-hidden-tablet');
      if (this.qvEl && !this.qvEl.hidden) document.body.classList.remove('overflow-hidden');
    }

    _updateViewportOffset() {
      const announcement = document.querySelector('.announcement-bar-spacer');
      const header = document.querySelector('.nb-header');
      const announcementH = announcement ? announcement.offsetHeight : 0;
      const headerH = header ? header.offsetHeight : 0;
      this.style.setProperty('--nb-bb-viewport-offset', `${announcementH + headerH}px`);
    }

    _readJson(name) {
      const el = this.querySelector(`script[type="application/json"][data-slot="${name}"]`);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.error(`[nb-bundle-builder] failed to parse ${name}:`, e);
        return null;
      }
    }

    _onClick(event) {
      const target = event.target.closest('[data-action]');
      if (!target || !this.contains(target)) return;
      const action = target.dataset.action;

      if (action === 'select-slide') {
        this._selectSlide(parseInt(target.dataset.slideIndex, 10));
        return;
      }
      if (action === 'add-all') {
        this._addAll(target);
        return;
      }
      if (action === 'add-to-bundle') {
        const card = target.closest('card-colors');
        const productId = target.dataset.productId;
        const variantId = this._resolveCardVariantId(card, productId, target.dataset.defaultVariantId);
        if (productId && variantId) {
          const isFirstPrimary =
            !this._scrolledToSecondary &&
            this.primaryProductIds.has(String(productId)) &&
            !Array.from(this.items.values()).some((it) => this.primaryProductIds.has(String(it.productId)));
          this._flashAddButton(target);
          this._addItem(String(productId), String(variantId));
          if (isFirstPrimary) {
            this._scrolledToSecondary = true;
            this._scrollToSecondary();
          }
        }
        return;
      }
      if (action === 'add-to-cart') {
        this._addToCart();
        return;
      }
      if (action === 'load-more') {
        this._loadMore(target);
        return;
      }
      if (action === 'filter-type') {
        this._applyFilter(target);
        return;
      }
      if (action === 'toggle-sidebar') {
        this._setSidebarExpanded(this._isSidebarExpanded() ? false : true);
        return;
      }
      if (action === 'close-sidebar') {
        this._setSidebarExpanded(false);
        return;
      }
      if (action === 'quickview') {
        const card = target.closest('card-colors');
        const addBtn = card && card.querySelector('[data-action="add-to-bundle"]');
        const productId = target.dataset.productId || (addBtn && addBtn.dataset.productId);
        const variantId = this._resolveCardVariantId(card, productId, addBtn && addBtn.dataset.defaultVariantId);
        this._openQuickview(productId, variantId);
        return;
      }
      if (action === 'quickview-close') {
        this._closeQuickview();
        return;
      }
      if (action === 'quickview-add') {
        this._quickviewAdd(target);
        return;
      }
      if (action === 'quickview-prev') {
        this._quickviewNav(-1);
        return;
      }
      if (action === 'quickview-next') {
        this._quickviewNav(1);
        return;
      }
      if (action === 'quickview-thumb') {
        if (this._qvState) {
          this._qvState.imageIndex = parseInt(target.dataset.index, 10) || 0;
          this._renderQuickviewGallery(false);
        }
        return;
      }
      if (action === 'quickview-select-option') {
        this._quickviewSelectOption(parseInt(target.dataset.optionIndex, 10), target.dataset.value);
        return;
      }

      const card = target.closest('card-colors');
      if (card && action === 'select-option') {
        const optionIndex = target.dataset.optionIndex;
        card
          .querySelectorAll(`.nb-bb__pill[data-option-index="${optionIndex}"]`)
          .forEach((p) => {
            p.dataset.active = p.dataset.value === target.dataset.value ? 'true' : 'false';
          });
        this._updateCardImage(card);
        return;
      }

      const row = target.closest('.nb-bb__row');
      if (!row) return;
      const key = row.dataset.key;

      if (action === 'qty-inc') {
        this._setQuantity(key, this.items.get(key).quantity + 1);
      } else if (action === 'qty-dec') {
        this._setQuantity(key, this.items.get(key).quantity - 1);
      } else if (action === 'remove') {
        this._removeItem(key);
      } else if (action === 'select-option') {
        const optionIndex = parseInt(target.dataset.optionIndex, 10);
        const value = target.dataset.value;
        this._changeVariantByOption(key, optionIndex, value);
      } else if (action === 'select-color') {
        this._changeVariantByOption(key, target.dataset.optionIndex ? parseInt(target.dataset.optionIndex, 10) : null, target.dataset.value);
      }
    }

    _onChange(event) {
      const t = event.target;
      if (t.matches('[data-slot="qv-swatch-radio"]')) {
        this._quickviewSelectOption(parseInt(t.dataset.optionIndex, 10), t.value);
        return;
      }
      if (t.matches('.nb-bb__row [data-slot="swatch-radio"]')) {
        const row = t.closest('.nb-bb__row');
        const key = row && row.dataset.key;
        if (!key) return;
        this._changeVariantByOption(key, parseInt(t.dataset.optionIndex, 10), t.value);
        return;
      }
      if (t.matches('.nb-bb-card [js-card-color-radio]')) {
        const card = t.closest('card-colors');
        if (!card) return;
        // Re-filter the print pills for the new color (may re-activate one), then refresh the image.
        this._updateCardOptions(card);
        this._updateCardImage(card);
      }
    }

    // ── Quickview modal ──────────────────────────────────────────────
    // Rendered once per section and driven by the same product JSON as the
    // cards, so "Add" goes through the exact same _addItem path.

    _openQuickview(productId, variantId) {
      const product = this._resolveProduct(productId);
      if (!product || !this.qvEl) return;
      const variant =
        product.variants.find((v) => String(v.id) === String(variantId)) ||
        product.variants.find((v) => !v.hidden && v.available) ||
        product.variants[0];
      if (!variant) return;
      this._qvState = { productId: String(productId), variantId: String(variant.id), imageIndex: 0 };
      this._renderQuickview();
      this._qvReturnFocus = document.activeElement;
      this.qvEl.hidden = false;
      document.body.classList.add('overflow-hidden');
      const dialog = this.qvEl.querySelector('.nb-bb-qv__dialog');
      if (typeof trapFocus === 'function' && dialog) {
        trapFocus(this.qvEl, dialog);
      } else if (dialog) {
        dialog.focus();
      }
    }

    _closeQuickview() {
      if (!this.qvEl || this.qvEl.hidden) return;
      this.qvEl.hidden = true;
      document.body.classList.remove('overflow-hidden');
      this._qvState = null;
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this._qvReturnFocus);
      } else if (this._qvReturnFocus && typeof this._qvReturnFocus.focus === 'function') {
        this._qvReturnFocus.focus();
      }
      this._qvReturnFocus = null;
    }

    _qvProductVariant() {
      if (!this._qvState) return {};
      const product = this._resolveProduct(this._qvState.productId);
      const variant = product && product.variants.find((v) => String(v.id) === String(this._qvState.variantId));
      return { product, variant };
    }

    _quickviewSelectOption(optionIndex, value) {
      const { product, variant } = this._qvProductVariant();
      if (!product || !variant) return;
      const desired = ['option1', 'option2', 'option3'].map((k, i) => (i === optionIndex ? value : variant[k]));
      let next = product.variants.find(
        (v) => !v.hidden && v.option1 === desired[0] && v.option2 === desired[1] && v.option3 === desired[2]
      );
      if (!next) {
        next = product.variants.find(
          (v) => !v.hidden && v.available && this._getVariantOptionValue(v, optionIndex) === value
        );
      }
      if (!next) {
        next = product.variants.find((v) => !v.hidden && this._getVariantOptionValue(v, optionIndex) === value);
      }
      if (!next) return;
      this._qvState.variantId = String(next.id);
      this._qvState.imageIndex = 0;
      this._renderQuickview();
    }

    _quickviewNav(delta) {
      if (!this._qvState || !this._qvImages || this._qvImages.length < 2) return;
      const len = this._qvImages.length;
      this._qvState.imageIndex = (this._qvState.imageIndex + delta + len) % len;
      this._renderQuickviewGallery(false);
    }

    _quickviewAdd(button) {
      if (!this._qvState) return;
      const { product, variant } = this._qvProductVariant();
      if (!product || !variant || !variant.available) return;
      const productId = this._qvState.productId;

      const cardButton = this.querySelector(`[data-action="add-to-bundle"][data-product-id="${productId}"]`);
      if (cardButton && cardButton.dataset.gated === 'true' && cardButton.disabled) return;

      const isFirstPrimary =
        !this._scrolledToSecondary &&
        this.primaryProductIds.has(String(productId)) &&
        !Array.from(this.items.values()).some((it) => this.primaryProductIds.has(String(it.productId)));

      this._addItem(productId, this._qvState.variantId);
      if (cardButton) this._flashAddButton(cardButton);

      button.disabled = true;
      button.textContent = 'Added';
      setTimeout(() => {
        this._closeQuickview();
        button.disabled = false;
        button.textContent = 'Add';
        if (isFirstPrimary) {
          this._scrolledToSecondary = true;
          this._scrollToSecondary();
        }
      }, 600);
    }

    _renderQuickview() {
      const { product, variant } = this._qvProductVariant();
      if (!product || !variant || !this.qvEl) return;

      this.qvEl.querySelector('[data-slot="qv-title"]').textContent = product.title;
      this.qvEl.querySelector('[data-slot="qv-price"]').textContent = this._formatMoney(variant.price);
      const link = this.qvEl.querySelector('[data-slot="qv-link"]');
      if (link) link.href = `${product.url}?variant=${variant.id}`;

      // Product dimensions (custom.dimensions metafield, pre-rendered as HTML).
      const dimsEl = this.qvEl.querySelector('[data-slot="qv-dimensions"]');
      if (dimsEl) {
        dimsEl.innerHTML = product.dimensions || '';
        dimsEl.hidden = !product.dimensions;
      }

      // Color swatches + "Select color" meta line, mirroring the sidebar rows.
      const colorIdx = this._findColorOptionIndex(product);
      const metaEl = this.qvEl.querySelector('[data-slot="qv-color-meta"]');
      const swatchesEl = this.qvEl.querySelector('[data-slot="qv-swatches"]');
      const colorWrapEl = this.qvEl.querySelector('[data-slot="qv-color"]');
      if (colorWrapEl) colorWrapEl.hidden = colorIdx < 0;
      swatchesEl.innerHTML = '';
      if (colorIdx >= 0) {
        const currentColor = this._getVariantOptionValue(variant, colorIdx);
        metaEl.hidden = false;
        this.qvEl.querySelector('[data-slot="qv-color-label"]').textContent = `Select ${(
          product.options[colorIdx] || 'Color'
        ).toLowerCase()}:`;
        this.qvEl.querySelector('[data-slot="qv-color-name"]').textContent = currentColor || '';
        const values = this._uniqueOptionValues(product, colorIdx);
        if (values.length > 1) {
          values.forEach((value) => {
            const isAvailable = product.variants.some(
              (v) => !v.hidden && v.available && this._getVariantOptionValue(v, colorIdx) === value
            );
            const label = document.createElement('label');
            label.className = 'nb-icon-filter-color' + (isAvailable ? '' : ' disabled');
            label.dataset.color = this._handleize(value);
            label.title = value;
            label.style.cursor = 'pointer';
            const input = document.createElement('input');
            input.type = 'radio';
            input.name = 'nb-bb-qv-color';
            input.value = value;
            input.dataset.slot = 'qv-swatch-radio';
            input.dataset.optionIndex = String(colorIdx);
            input.checked = value === currentColor;
            input.style.position = 'absolute';
            input.style.opacity = '0';
            input.style.pointerEvents = 'none';
            label.appendChild(input);
            swatchesEl.appendChild(label);
          });
        }
      } else {
        metaEl.hidden = true;
      }

      // Non-color option pills, filtered to the selected color (same as rows).
      const pillsEl = this.qvEl.querySelector('[data-slot="qv-pills"]');
      pillsEl.innerHTML = '';
      const matchesColor = (v) =>
        colorIdx < 0 || this._getVariantOptionValue(v, colorIdx) === this._getVariantOptionValue(variant, colorIdx);
      product.options.forEach((optName, idx) => {
        if (idx === colorIdx) return;
        const values = this._uniqueOptionValues(product, idx).filter((value) =>
          product.variants.some((v) => !v.hidden && matchesColor(v) && this._getVariantOptionValue(v, idx) === value)
        );
        if (values.length <= 1) return;
        values.forEach((value) => {
          const matched = product.variants.find(
            (v) => !v.hidden && matchesColor(v) && this._getVariantOptionValue(v, idx) === value
          );
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nb-bb__pill';
          btn.dataset.action = 'quickview-select-option';
          btn.dataset.optionIndex = String(idx);
          btn.dataset.value = value;
          btn.dataset.active = value === this._getVariantOptionValue(variant, idx) ? 'true' : 'false';
          if (!(matched && matched.available)) btn.disabled = true;
          btn.textContent = value;
          pillsEl.appendChild(btn);
        });
      });

      // Add button + gate note, mirroring the grid card's state.
      const addBtn = this.qvEl.querySelector('[data-action="quickview-add"]');
      const gateNote = this.qvEl.querySelector('[data-slot="qv-gate-note"]');
      const cardButton = this.querySelector(
        `[data-action="add-to-bundle"][data-product-id="${this._qvState.productId}"]`
      );
      const gateLocked = !!(cardButton && cardButton.dataset.gated === 'true' && cardButton.disabled);
      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.textContent = 'Sold out';
        gateNote.hidden = true;
      } else {
        addBtn.disabled = gateLocked;
        addBtn.textContent = 'Add';
        gateNote.hidden = !gateLocked;
        if (gateLocked) gateNote.textContent = this.dataset.quickviewGatedLabel || 'Add a required item first';
      }

      // Gallery: the selected variant's metafield images first, then only the media
      // whose alt text names this variant (the store tags each shot with its variant
      // title, e.g. "Walnut / Celadon Gingham"). If no media is tagged for this
      // variant, fall back to the product-level images and full media list.
      const images = [];
      const pushImage = (src) => {
        if (src && !images.includes(src)) images.push(src);
      };
      const normalize = (s) =>
        String(s || '')
          .replace(/\u00a0/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();
      const media = (product.media || []).map((m) => (typeof m === 'string' ? { src: m, alt: '' } : m));
      const variantMedia = media.filter((m) => m && normalize(m.alt) === normalize(variant.title));

      [variant.image_1, variant.image_main].forEach(pushImage);
      if (variantMedia.length) {
        variantMedia.forEach((m) => pushImage(m.src));
      } else {
        [product.image_1, product.image_main, product.image].forEach(pushImage);
        media.forEach((m) => pushImage(m && m.src));
      }
      this._qvImages = images;
      if (this._qvState.imageIndex >= images.length) this._qvState.imageIndex = 0;
      const stageImg = this.qvEl.querySelector('[data-slot="qv-image"]');
      if (stageImg) stageImg.alt = product.title;
      this._renderQuickviewGallery(true);
    }

    _renderQuickviewGallery(rebuildThumbs) {
      if (!this.qvEl) return;
      const images = this._qvImages || [];
      const index = this._qvState ? this._qvState.imageIndex : 0;
      const stageImg = this.qvEl.querySelector('[data-slot="qv-image"]');
      const thumbsEl = this.qvEl.querySelector('[data-slot="qv-thumbs"]');

      if (stageImg && images[index]) stageImg.src = images[index];
      this.qvEl.querySelectorAll('.nb-bb-qv__nav').forEach((btn) => {
        btn.hidden = images.length < 2;
      });

      if (rebuildThumbs) {
        thumbsEl.innerHTML = '';
        if (images.length > 1) {
          images.forEach((src, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'nb-bb-qv__thumb';
            btn.dataset.action = 'quickview-thumb';
            btn.dataset.index = String(i);
            btn.setAttribute('aria-label', `Image ${i + 1}`);
            const img = document.createElement('img');
            img.src = src;
            img.alt = '';
            img.loading = 'lazy';
            btn.appendChild(img);
            thumbsEl.appendChild(btn);
          });
        }
      }
      thumbsEl.querySelectorAll('.nb-bb-qv__thumb').forEach((btn) => {
        btn.setAttribute('aria-current', parseInt(btn.dataset.index, 10) === index ? 'true' : 'false');
      });
    }

    async _loadMore(button) {
      if (button.disabled) return;
      const collectionHandle = button.dataset.collectionHandle;
      const sectionId = this.dataset.sectionId;
      const nextPage = parseInt(button.dataset.nextPage, 10);
      const totalPages = parseInt(button.dataset.totalPages, 10);
      if (!collectionHandle || !sectionId || !nextPage) return;

      button.disabled = true;
      button.classList.add('loading');

      try {
        const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}&page=${nextPage}`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();

        const tmp = document.createElement('div');
        tmp.innerHTML = html;

        const newGrid = tmp.querySelector(`.nb-bb__save-grid[data-collection-handle="${collectionHandle}"]`);
        const existingGrid = this.querySelector(`.nb-bb__save-grid[data-collection-handle="${collectionHandle}"]`);
        if (!newGrid || !existingGrid) throw new Error('grid not found');

        const newCards = Array.from(newGrid.children);
        newCards.forEach((card) => existingGrid.appendChild(card));
        newCards.forEach((card) => this._updateCardOptions(card));

        const footer = button.closest('.nb-bb__save-footer');
        const shownEl = footer && footer.querySelector('[data-slot="save-shown"]');
        const totalEl = footer && footer.querySelector('[data-slot="save-total"]');
        if (shownEl && totalEl) {
          const total = parseInt(totalEl.textContent, 10);
          const newShown = Math.min(parseInt(shownEl.textContent, 10) + newCards.length, total);
          shownEl.textContent = newShown;
        }

        const newNext = nextPage + 1;
        if (newNext > totalPages) {
          button.remove();
        } else {
          button.dataset.nextPage = String(newNext);
          button.disabled = false;
        }

        this._updatePrimaryGates();
      } catch (err) {
        console.error('[nb-bundle-builder] load more failed:', err);
        button.disabled = false;
      } finally {
        button.classList.remove('loading');
      }
    }

    async _applyFilter(button) {
      const saveSection = button.closest('.nb-bb__save');
      if (!saveSection) return;
      const type = button.dataset.type || '';

      saveSection.querySelectorAll('[data-action="filter-type"]').forEach((b) => {
        b.dataset.active = b === button ? 'true' : 'false';
      });

      // A type filter must see every product, so pull in any remaining pages first.
      // "All" needs nothing extra — just reveal what's already loaded.
      if (type) {
        const filters = saveSection.querySelector('.nb-bb__filters');
        if (filters) filters.dataset.loading = 'true';
        try {
          await this._loadAllPages(saveSection);
        } finally {
          if (filters) filters.removeAttribute('data-loading');
        }
      }

      this._filterCards(saveSection, type);
    }

    async _loadAllPages(saveSection) {
      const grid = saveSection.querySelector('.nb-bb__save-grid');
      if (!grid || grid.dataset.allLoaded === 'true') return;
      let button = saveSection.querySelector('[data-action="load-more"]');
      while (button && !button.disabled) {
        await this._loadMore(button);
        button = saveSection.querySelector('[data-action="load-more"]');
      }
      grid.dataset.allLoaded = 'true';
    }

    _filterCards(saveSection, type) {
      const grid = saveSection.querySelector('.nb-bb__save-grid');
      if (!grid) return;
      const cards = Array.from(grid.querySelectorAll('.nb-bb-card'));
      let visible = 0;
      cards.forEach((card) => {
        const match = !type || card.dataset.productType === type;
        card.hidden = !match;
        if (match) visible += 1;
      });

      const footer = saveSection.querySelector('.nb-bb__save-footer');
      if (!footer) return;
      const loadMore = footer.querySelector('[data-action="load-more"]');
      const shownEl = footer.querySelector('[data-slot="save-shown"]');
      const totalEl = footer.querySelector('[data-slot="save-total"]');
      // Stash the collection's true total once so we can restore it on "All".
      if (totalEl && !footer.dataset.gridTotal) footer.dataset.gridTotal = totalEl.textContent.trim();

      if (type) {
        if (loadMore) loadMore.hidden = true;
        if (shownEl) shownEl.textContent = String(visible);
        if (totalEl) totalEl.textContent = String(visible);
      } else {
        const allLoaded = grid.dataset.allLoaded === 'true';
        if (shownEl) shownEl.textContent = String(cards.length);
        if (totalEl) totalEl.textContent = allLoaded ? String(cards.length) : footer.dataset.gridTotal || String(cards.length);
        if (loadMore) loadMore.hidden = allLoaded;
      }
    }

    _updateCardImage(card) {
      if (!card) return;
      const variant = this._resolveCardVariant(card);
      if (!variant) return;
      const img1 = card.querySelector('.nb-bb-card__media [data-image1]');
      const img2 = card.querySelector('.nb-bb-card__media [data-image2]');
      const src1 = variant.image_1 || variant.image_main;
      const src2 = variant.image_main || variant.image_1;
      if (img1 && src1) {
        img1.removeAttribute('srcset');
        img1.removeAttribute('sizes');
        img1.src = src1;
      }
      if (img2 && src2) {
        img2.removeAttribute('srcset');
        img2.removeAttribute('sizes');
        img2.src = src2;
      }
      this._updateCardPrice(card, variant);
    }

    _updateCardPrice(card, variant) {
      if (!card) return;
      if (!variant) variant = this._resolveCardVariant(card);
      if (!variant) return;
      const priceEl = card.querySelector('[data-slot="card-price"]');
      if (priceEl) priceEl.textContent = this._formatMoney(variant.price);
    }

    _resolveCardVariant(card) {
      if (!card) return null;
      const addBtn = card.querySelector('[data-action="add-to-bundle"]');
      if (!addBtn) return null;
      const productId = addBtn.dataset.productId;
      const product = this._resolveProduct(productId);
      if (!product) return null;
      const variantId = this._resolveCardVariantId(card, productId, addBtn.dataset.defaultVariantId);
      return product.variants.find((v) => String(v.id) === String(variantId)) || null;
    }

    // Show/hide a card's secondary-option pills (e.g. Print) based on the chosen color, so only the
    // values that have a variant for that color appear. Existing-but-sold-out pills stay visible and
    // disabled; if the active pill gets hidden, fall back to the first usable one. Mirrors the
    // per-row filtering in _renderItemRow.
    _updateCardOptions(card) {
      if (!card) return;
      const addBtn = card.querySelector('[data-action="add-to-bundle"]');
      const product = this._resolveProduct(addBtn && addBtn.dataset.productId);
      if (!product) return;

      const colorIdx = this._findColorOptionIndex(product);
      const checkedColor = card.querySelector('[js-card-color-radio]:checked');
      const startVariantId = (checkedColor && checkedColor.dataset.variantId) || (addBtn && addBtn.dataset.defaultVariantId);
      const startVariant = product.variants.find((v) => String(v.id) === String(startVariantId));
      const selectedColor = startVariant && colorIdx >= 0 ? this._getVariantOptionValue(startVariant, colorIdx) : null;

      card.querySelectorAll('.nb-bb-card__pill-row').forEach((row) => {
        const pills = Array.from(row.querySelectorAll('.nb-bb__pill'));

        pills.forEach((pill) => {
          const idx = parseInt(pill.dataset.optionIndex, 10);
          const matches = (requireAvailable) =>
            product.variants.some((v) => {
              if (v.hidden) return false;
              if (requireAvailable && !v.available) return false;
              if (this._getVariantOptionValue(v, idx) !== pill.dataset.value) return false;
              if (selectedColor != null && this._getVariantOptionValue(v, colorIdx) !== selectedColor) return false;
              return true;
            });
          pill.hidden = !matches(false);
          pill.disabled = !matches(true);
        });

        const active = pills.find((p) => p.dataset.active === 'true');
        if (!active || active.hidden) {
          const fallback = pills.find((p) => !p.hidden && !p.disabled) || pills.find((p) => !p.hidden);
          pills.forEach((p) => (p.dataset.active = p === fallback ? 'true' : 'false'));
        }
      });
    }

    _initCards(root) {
      (root || this).querySelectorAll('card-colors').forEach((card) => this._updateCardOptions(card));
    }

    _resolveCardVariantId(card, productId, fallbackVariantId) {
      if (!productId) return fallbackVariantId || null;
      const product = this._resolveProduct(productId);
      if (!product) return fallbackVariantId || null;

      const checkedColor = card && card.querySelector('[js-card-color-radio]:checked');
      const startVariantId = (checkedColor && checkedColor.dataset.variantId) || fallbackVariantId;
      const startVariant = product.variants.find((v) => String(v.id) === String(startVariantId));
      if (!startVariant) return fallbackVariantId || null;

      const desired = [startVariant.option1, startVariant.option2, startVariant.option3];
      if (card) {
        card.querySelectorAll('.nb-bb__pill[data-active="true"]').forEach((pill) => {
          const idx = parseInt(pill.dataset.optionIndex, 10);
          if (!isNaN(idx) && idx >= 0 && idx < 3) desired[idx] = pill.dataset.value;
        });
      }

      const match = product.variants.find(
        (v) =>
          !v.hidden &&
          v.option1 === desired[0] &&
          v.option2 === desired[1] &&
          v.option3 === desired[2]
      );
      return match ? String(match.id) : String(startVariant.id);
    }

    _isSidebarExpanded() {
      const sidebar = this.querySelector('.nb-bb__sidebar');
      return !!(sidebar && sidebar.dataset.expanded === 'true');
    }

    _setSidebarExpanded(expanded) {
      const sidebar = this.querySelector('.nb-bb__sidebar');
      const toggle = this.querySelector('[data-action="toggle-sidebar"]');
      const overlay = this.querySelector('.nb-bb__sidebar-overlay');
      if (!sidebar) return;
      sidebar.dataset.expanded = expanded ? 'true' : 'false';
      if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (overlay) {
        if (expanded) {
          overlay.hidden = false;
          requestAnimationFrame(() => overlay.setAttribute('data-visible', 'true'));
        } else {
          overlay.setAttribute('data-visible', 'false');
          overlay.hidden = true;
        }
      }
      document.body.classList.toggle('overflow-hidden-tablet', expanded);
    }

    _flashAddButton(button) {
      if (!button) return;
      if (!button.dataset.originalLabel) {
        button.dataset.originalLabel = button.textContent.trim();
      }
      const original = button.dataset.originalLabel;
      if (button._flashTimers) button._flashTimers.forEach((t) => clearTimeout(t));
      button._flashTimers = [];

      button.textContent = 'Adding...';
      button._flashTimers.push(
        setTimeout(() => {
          button.textContent = 'Added';
        }, 300)
      );
      button._flashTimers.push(
        setTimeout(() => {
          button.textContent = original;
          button._flashTimers = [];
        }, 1500)
      );
    }

    _scrollToSecondary() {
      const saveSections = this.querySelectorAll('.nb-bb__save');
      const secondary = Array.from(saveSections).find((s) => s.querySelector('[data-gated="true"]'));
      if (!secondary) return;
      const announcement = document.querySelector('.announcement-bar-spacer');
      const header = document.querySelector('.nb-header');
      const offset = (announcement ? announcement.offsetHeight : 0) + (header ? header.offsetHeight : 0) + 20;
      const top = secondary.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    _selectSlide(index) {
      this._setActiveSlide(index);
      if (this.stageTrackEl) {
        this.stageTrackEl.scrollTo({ left: index * this.stageTrackEl.clientWidth, behavior: 'smooth' });
      }
    }

    _setActiveSlide(index) {
      this.currentSlide = index;
      if (!this.stageEl) return;
      this.thumbButtons.forEach((b) => {
        b.setAttribute('aria-current', parseInt(b.dataset.slideIndex, 10) === index ? 'true' : 'false');
      });
      this.stageEl.querySelectorAll('[data-slide-index]').forEach((el) => {
        el.hidden = parseInt(el.dataset.slideIndex, 10) !== index;
      });
    }

    _onStageScroll() {
      if (this._stageScrollRaf) return;
      this._stageScrollRaf = requestAnimationFrame(() => {
        this._stageScrollRaf = null;
        const track = this.stageTrackEl;
        if (!track || !track.clientWidth) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        if (index !== this.currentSlide) this._setActiveSlide(index);
      });
    }

    _findColorOptionIndex(product) {
      if (!product || !product.options) return -1;
      return product.options.findIndex((n) => /color/i.test(n));
    }

    _getVariantOptionValue(variant, index) {
      return variant['option' + (index + 1)];
    }

    _resolveProduct(productId) {
      return this.productIndex[String(productId)] || null;
    }

    _addItem(productId, variantId) {
      const product = this._resolveProduct(productId);
      if (!product) {
        console.warn('[nb-bundle-builder] unknown product', productId);
        return false;
      }
      const variant = product.variants.find((v) => String(v.id) === String(variantId));
      if (!variant || !variant.available) {
        console.warn('[nb-bundle-builder] variant unavailable', productId, variantId);
        return false;
      }
      const key = `${productId}::${variantId}`;
      if (this.items.has(key)) {
        const existing = this.items.get(key);
        existing.quantity += 1;
      } else {
        this.items.set(key, {
          lineKey: key,
          productId: String(productId),
          variantId: String(variantId),
          quantity: 1,
          product,
        });
      }
      this._render();
      return true;
    }

    _removeItem(key) {
      this.items.delete(key);
      this._render();
    }

    _setQuantity(key, qty) {
      if (qty <= 0) return this._removeItem(key);
      const item = this.items.get(key);
      if (!item) return;
      item.quantity = qty;
      this._render();
    }

    _changeVariantByOption(key, optionIndex, value) {
      const item = this.items.get(key);
      if (!item) return;
      const product = item.product;
      const currentVariant = product.variants.find((v) => String(v.id) === String(item.variantId));
      if (!currentVariant) return;

      const desired = ['option1', 'option2', 'option3'].map((k, i) =>
        i === optionIndex ? value : currentVariant[k]
      );
      let next = product.variants.find(
        (v) => !v.hidden && v.option1 === desired[0] && v.option2 === desired[1] && v.option3 === desired[2]
      );
      if (!next) {
        // fallback: relax other options, pick first variant matching the changed option that's available
        next = product.variants.find(
          (v) => !v.hidden && v.available && this._getVariantOptionValue(v, optionIndex) === value
        );
      }
      if (!next) return;

      const newKey = `${item.productId}::${next.id}`;
      if (newKey === key) return;

      // Rebuild the map preserving insertion order so the row doesn't jump to the bottom.
      const entries = Array.from(this.items.entries());
      const idx = entries.findIndex(([k]) => k === key);

      if (this.items.has(newKey)) {
        this.items.get(newKey).quantity += item.quantity;
        entries.splice(idx, 1);
      } else {
        entries[idx] = [
          newKey,
          {
            lineKey: newKey,
            productId: item.productId,
            variantId: String(next.id),
            quantity: item.quantity,
            product,
          },
        ];
      }
      this.items = new Map(entries);
      this._render();
    }

    _addAll(button) {
      const slideVariants = this.galleryVariants[this.currentSlide] || [];
      if (!slideVariants.length) {
        console.warn(
          `[nb-bundle-builder] slide ${this.currentSlide} has no nb_gallery_variants; "Add all" has nothing to add.`
        );
        return;
      }

      let added = 0;
      slideVariants.forEach((variantId) => {
        const productId = this._findProductIdByVariantId(variantId);
        if (!productId) {
          console.warn('[nb-bundle-builder] add-all variant not in the bundle collections, skipped', variantId);
          return;
        }
        if (this._addItem(String(productId), String(variantId))) added += 1;
      });

      if (added > 0) {
        this._flashAddButton(button);
      } else {
        console.warn('[nb-bundle-builder] add-all: none of the slide variants could be added to the bundle');
      }
    }

    _findProductIdByVariantId(variantId) {
      for (const [pid, product] of Object.entries(this.productIndex)) {
        if (product.variants && product.variants.some((v) => String(v.id) === String(variantId))) return pid;
      }
      return null;
    }

    // Refresh/open the cart after a custom /cart/add.js. This site's cart UI is Rebuy
    // Smart Cart (the header cart button calls window.Rebuy.SmartCart.show()), so there
    // is no Dawn <cart-drawer> element on these pages — query it directly and it returns
    // null, leaving no feedback. Notify the theme via the shared pub/sub event (same as
    // crib-collection-tracker.js) and drive Rebuy, falling back to Dawn's drawer if present.
    _afterCartAdd(data) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nb-bundle-builder', cartData: data });
      }

      const smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (smartCart) {
        if (typeof smartCart.refresh === 'function') smartCart.refresh();
        if (typeof smartCart.show === 'function') smartCart.show();
        return;
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!cart) return;
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      if (typeof cart.renderContents === 'function' && data && data.sections) {
        cart.renderContents(data);
      } else if (typeof cart.open === 'function') {
        cart.open();
      }
    }

    async _addToCart() {
      if (this.items.size === 0) return;
      const stamp = Date.now().toString(36);
      const payload = {
        items: Array.from(this.items.values()).map((it) => {
          const variant = it.product.variants.find((v) => String(v.id) === String(it.variantId));
          const properties = { '_bundle_group': stamp };
          if (this.dataset.bundleTitle) properties['_bundle_title'] = this.dataset.bundleTitle;
          if (variant && variant.eta) properties['ETA'] = variant.eta;
          if (variant && variant.shipping) properties['Shipping'] = variant.shipping;
          return {
            id: Number(it.variantId),
            quantity: it.quantity,
            properties,
          };
        }),
        sections: 'cart-icon-bubble',
      };

      this.atcEl.setAttribute('disabled', 'disabled');
      this.atcEl.classList.add('loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || 'Failed to add to cart');
        }
        const data = await response.json();
        this._afterCartAdd(data);
        this.items.clear();
        this._render();
      } catch (err) {
        console.error('[nb-bundle-builder] add-to-cart failed:', err);
        alert('Could not add bundle to cart. Please try again.');
      } finally {
        this.atcEl.classList.remove('loading');
        this._render();
      }
    }

    _render() {
      const hasItems = this.items.size > 0;
      this.emptyEl.hidden = hasItems;
      this.atcEl.disabled = !hasItems;

      const totalQty = this._totalQty();
      if (this.countEl) this.countEl.textContent = String(totalQty);

      this.activeTier = this._getActiveTier();
      this._giftFree = this._computeGiftAllocation();
      this._renderRows();
      this._renderSummary();
      this._updateProgressBar();
      this._updatePrimaryGates();
    }

    _renderSummary() {
      if (!this.summaryEl) return;
      const hasItems = this.items.size > 0;
      this.summaryEl.hidden = !hasItems;
      if (!hasItems) return;

      let subtotal = 0;
      let total = 0;
      for (const item of this.items.values()) {
        const variant = item.product.variants.find((v) => String(v.id) === String(item.variantId));
        if (!variant) continue;
        const percent = this._tierPercentFor(item.productId);
        const unitPrice = percent > 0 ? Math.round(variant.price * (1 - percent / 100)) : variant.price;
        const gift = this._giftAllocFor(item.lineKey);
        subtotal += variant.price * item.quantity;
        if (gift) {
          const giftUnitPrice = Math.round(unitPrice * (1 - gift.percent / 100));
          total += unitPrice * (item.quantity - gift.units) + giftUnitPrice * gift.units;
        } else {
          total += unitPrice * item.quantity;
        }
      }
      const savings = subtotal - total;

      if (this.subtotalEl) this.subtotalEl.textContent = this._formatMoneyFixed(subtotal);
      if (this.totalEl) this.totalEl.textContent = this._formatMoneyFixed(total);
      if (this.savingsRowEl) this.savingsRowEl.hidden = savings <= 0;
      if (this.savingsEl) this.savingsEl.textContent = '- ' + this._formatMoneyFixed(savings);
    }

    _formatMoneyFixed(cents) {
      return `$${(cents / 100).toFixed(2)}`;
    }

    // Discount Kit discount_summary metaobjects picked in the section settings
    // (data-slot="dk-discount-configs") carry the app's full configuration JSON.
    // Distill its buy-X-get-Y shape — bucketRules name product groups,
    // discountRules pair a prereq bucket with a target bucket and a percentage —
    // into flat rules the builder can preview. Display-only: Discount Kit
    // applies the real discount at checkout.
    _parseGiftRules(configs) {
      const rules = [];
      configs.filter(Boolean).forEach((config) => {
        const buckets = {};
        (config.bucketRules || []).forEach((bucket) => {
          if (bucket && bucket.name && bucket.productFilter) {
            buckets[bucket.name] = bucket.productFilter.map((p) => String(p.id));
          }
        });
        (config.discountRules || []).forEach((rule) => {
          const prereq = (rule.prereqParts || [])[0];
          const target = (rule.targetParts || [])[0];
          if (!prereq || !target) return;
          const percentValue =
            rule.discountValue && rule.discountValue.percentage && rule.discountValue.percentage.value;
          const percent = Number(percentValue);
          if (!(percent > 0)) return;
          rules.push({
            prereqProducts: buckets[prereq.bucket] || [],
            prereqQty: Number(prereq.quantity) || 1,
            targetProducts: buckets[target.bucket] || [],
            targetQty: Number(target.quantity) || 1,
            percent,
            recurring: rule.recurring !== false,
            message: rule.message || '',
          });
        });
      });
      return rules;
    }

    // Allocate each rule's discounted units to the bundle's target lines,
    // cheapest first. Returns a Map of lineKey -> { units, percent }.
    _computeGiftAllocation() {
      const alloc = new Map();
      if (!this.giftRules || this.giftRules.length === 0) return alloc;
      const items = Array.from(this.items.values());

      for (const rule of this.giftRules) {
        const prereqUnits = items
          .filter((it) => rule.prereqProducts.indexOf(String(it.productId)) !== -1)
          .reduce((sum, it) => sum + it.quantity, 0);
        if (prereqUnits < rule.prereqQty) continue;
        let grant = rule.recurring ? Math.floor(prereqUnits / rule.prereqQty) * rule.targetQty : rule.targetQty;

        const targets = items
          .filter((it) => rule.targetProducts.indexOf(String(it.productId)) !== -1)
          .map((it) => {
            const variant = it.product.variants.find((v) => String(v.id) === String(it.variantId));
            return { it, price: variant ? variant.price : 0 };
          })
          .sort((a, b) => a.price - b.price);

        for (const { it } of targets) {
          if (grant <= 0) break;
          const existing = alloc.get(it.lineKey);
          const already = existing ? existing.units : 0;
          const take = Math.min(it.quantity - already, grant);
          if (take > 0) {
            alloc.set(it.lineKey, {
              units: already + take,
              percent: existing ? Math.max(existing.percent, rule.percent) : rule.percent,
            });
            grant -= take;
          }
        }
      }
      return alloc;
    }

    _giftAllocFor(lineKey) {
      return (this._giftFree && this._giftFree.get(lineKey)) || null;
    }

    _giftMessageFor(productId) {
      const rule = (this.giftRules || []).find((r) => r.targetProducts.indexOf(String(productId)) !== -1);
      return (rule && rule.message) || '';
    }

    // Active tier percent for a product. Primary (first collection) items only
    // get the discount when the section's discount_primary setting is on.
    _tierPercentFor(productId) {
      if (!this.activeTier) return 0;
      if (!this.discountPrimary && this.primaryProductIds.has(String(productId))) return 0;
      return Number(this.activeTier.percent) || 0;
    }

    // Tier eligibility counts total units, not distinct products — two of the
    // same cubby qualifies for the "2+" discount just like two different items.
    _totalQty() {
      return Array.from(this.items.values()).reduce((sum, it) => sum + it.quantity, 0);
    }

    _getActiveTier() {
      if (!this.tiers || this.tiers.length === 0) return null;
      const count = this._totalQty();
      let activeIndex = -1;
      this.tiers.forEach((t, i) => {
        if (count >= t.count) activeIndex = i;
      });
      return activeIndex >= 0 ? this.tiers[activeIndex] : null;
    }

    _updatePrimaryGates() {
      const hasPrimary = Array.from(this.items.values()).some((it) =>
        this.primaryProductIds.has(String(it.productId))
      );
      this.querySelectorAll('[data-gated="true"]').forEach((btn) => {
        if (btn.dataset.unavailable === 'true') {
          btn.disabled = true;
          return;
        }
        btn.disabled = !hasPrimary;
      });
    }

    _renderRows() {
      const existing = new Map();
      this.itemsEl.querySelectorAll('.nb-bb__row').forEach((r) => existing.set(r.dataset.key, r));

      for (const key of existing.keys()) {
        if (!this.items.has(key)) {
          existing.get(key).remove();
          existing.delete(key);
        }
      }

      for (const item of this.items.values()) {
        let row = existing.get(item.lineKey);
        if (!row) row = this._buildRow(item);
        this.itemsEl.appendChild(row);
        this._updateRow(row, item);
      }
    }

    _buildRow(item) {
      const frag = this.rowTpl.content.cloneNode(true);
      const row = frag.querySelector('.nb-bb__row');
      row.dataset.key = item.lineKey;
      return row;
    }

    _updateRow(row, item) {
      const product = item.product;
      const variant = product.variants.find((v) => String(v.id) === String(item.variantId));
      if (!variant) return;

      const imgEl = row.querySelector('[data-slot="image"]');
      // Same preference order as the grid cards: image_1 metafield first,
      // then image_main, with the featured image as a last resort.
      const imgSrc =
        variant.image_1 || product.image_1 || variant.image_main || product.image_main || product.image || '';
      if (imgSrc) imgEl.src = imgSrc;
      imgEl.alt = product.title;

      row.querySelector('[data-slot="title"]').textContent = product.title;
      const percent = this._tierPercentFor(item.productId);
      const displayPrice = percent > 0 ? Math.round(variant.price * (1 - percent / 100)) : variant.price;
      const gift = this._giftAllocFor(item.lineKey);
      const priceEl = row.querySelector('[data-slot="price"]');
      priceEl.textContent = '';
      if (gift && gift.units >= item.quantity && gift.percent >= 100) {
        const compare = document.createElement('s');
        compare.className = 'nb-bb__row-price-compare';
        compare.textContent = this._formatMoney(variant.price);
        priceEl.appendChild(compare);
        priceEl.appendChild(document.createTextNode(' Free'));
        priceEl.title = this._giftMessageFor(item.productId);
      } else {
        priceEl.title = '';
        if (percent > 0) {
          const compare = document.createElement('s');
          compare.className = 'nb-bb__row-price-compare';
          compare.textContent = this._formatMoney(variant.price);
          priceEl.appendChild(compare);
          priceEl.appendChild(document.createTextNode(' ' + this._formatMoney(displayPrice)));
        } else {
          priceEl.textContent = this._formatMoney(variant.price);
        }
        if (gift && gift.units > 0) {
          const note = gift.percent >= 100 ? `(${gift.units} free)` : `(${gift.units} at ${gift.percent}% off)`;
          priceEl.appendChild(document.createTextNode(' ' + note));
        }
      }
      row.querySelector('[data-slot="qty"]').textContent = String(item.quantity);

      const colorIdx = this._findColorOptionIndex(product);
      const colorMetaEl = row.querySelector('[data-slot="color-meta"]');
      const colorNameEl = row.querySelector('[data-slot="color-name"]');
      const swatchesEl = row.querySelector('[data-slot="swatches"]');
      const pillsEl = row.querySelector('[data-slot="pills"]');
      swatchesEl.innerHTML = '';
      pillsEl.innerHTML = '';

      if (colorIdx >= 0) {
        const currentColor = this._getVariantOptionValue(variant, colorIdx);
        colorMetaEl.hidden = false;
        const colorLabelEl = row.querySelector('[data-slot="color-label"]');
        if (colorLabelEl) colorLabelEl.textContent = (product.options[colorIdx] || 'Color') + ':';
        colorNameEl.textContent = currentColor || '';
        const colorValues = this._uniqueOptionValues(product, colorIdx);
        colorValues.forEach((value) => {
          const isAvailable = product.variants.some(
            (v) => !v.hidden && v.available && this._getVariantOptionValue(v, colorIdx) === value
          );
          const label = document.createElement('label');
          label.className = 'nb-icon-filter-color' + (isAvailable ? '' : ' disabled');
          label.dataset.color = this._handleize(value);
          label.title = value;
          label.style.cursor = 'pointer';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `nb-bb-row-color-${item.lineKey}-${colorIdx}`;
          input.value = value;
          input.dataset.slot = 'swatch-radio';
          input.dataset.optionIndex = String(colorIdx);
          input.checked = value === currentColor;
          input.style.position = 'absolute';
          input.style.opacity = '0';
          input.style.pointerEvents = 'none';
          label.appendChild(input);
          swatchesEl.appendChild(label);
        });
      } else {
        colorMetaEl.hidden = true;
      }

      product.options.forEach((optName, idx) => {
        if (idx === colorIdx) return;
        const values = this._uniqueOptionValues(product, idx).filter((value) => {
          return product.variants.some((v) => {
            if (v.hidden) return false;
            if (this._getVariantOptionValue(v, idx) !== value) return false;
            if (colorIdx >= 0 && this._getVariantOptionValue(v, colorIdx) !== this._getVariantOptionValue(variant, colorIdx)) return false;
            return true;
          });
        });
        if (values.length <= 1) return;
        values.forEach((value) => {
          const matched = product.variants.find((v) => {
            if (v.hidden) return false;
            if (this._getVariantOptionValue(v, idx) !== value) return false;
            if (colorIdx >= 0 && this._getVariantOptionValue(v, colorIdx) !== this._getVariantOptionValue(variant, colorIdx)) return false;
            return true;
          });
          const isAvailable = !!(matched && matched.available);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nb-bb__pill';
          btn.dataset.action = 'select-option';
          btn.dataset.optionIndex = String(idx);
          btn.dataset.value = value;
          btn.dataset.active = value === this._getVariantOptionValue(variant, idx) ? 'true' : 'false';
          if (!isAvailable) btn.disabled = true;
          btn.textContent = value;
          pillsEl.appendChild(btn);
        });
      });
    }

    _uniqueOptionValues(product, optionIndex) {
      const seen = new Set();
      const values = [];
      product.variants.forEach((v) => {
        if (v.hidden) return;
        const val = this._getVariantOptionValue(v, optionIndex);
        if (val && !seen.has(val)) {
          seen.add(val);
          values.push(val);
        }
      });
      return values;
    }

    _handleize(str) {
      return String(str || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    _formatMoney(cents) {
      const dollars = cents / 100;
      if (cents % 100 === 0) return `$${dollars}`;
      return `$${dollars.toFixed(2)}`;
    }

    _updateProgressBar() {
      if (!this.tiers || this.tiers.length === 0) return;
      const count = this._totalQty();
      const tiers = this.tiers;
      const n = tiers.length;
      const activeTier = this.activeTier;
      const activeIndex = activeTier ? tiers.indexOf(activeTier) : -1;

      let progress;
      if (n === 1) {
        progress = count >= tiers[0].count ? 100 : 0;
      } else if (activeIndex === -1) {
        const tFrac = tiers[0].count > 0 ? Math.min(1, count / tiers[0].count) : 0;
        progress = tFrac * (1 / (n - 1)) * 100;
      } else if (activeIndex === n - 1) {
        progress = 100;
      } else {
        const prev = tiers[activeIndex];
        const next = tiers[activeIndex + 1];
        const span = next.count - prev.count;
        const tFrac = span > 0 ? (count - prev.count) / span : 0;
        progress = ((activeIndex + tFrac) / (n - 1)) * 100;
      }
      if (this.progressFill) this.progressFill.style.width = progress + '%';

      this.tierDots.forEach((dot) => {
        const c = parseInt(dot.dataset.tierCount, 10);
        dot.dataset.active = count >= c ? 'true' : 'false';
      });
      this.tierLabels.forEach((lab) => {
        const c = parseInt(lab.dataset.tierCount, 10);
        lab.dataset.active = activeTier && c === activeTier.count ? 'true' : 'false';
      });
    }
  }

  if (!customElements.get('nb-bundle-builder')) {
    customElements.define('nb-bundle-builder', NbBundleBuilder);
  }
}
